import { useEffect, useRef, useState } from 'react';

// PrimeReact components
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';

// Column definitions (already declared in appconfig)
import { PciListColumns } from '../../appconfig/DatatableSetting';

// Data table settings
import { defaultRowOptions, statusFilterOptions, defaultPageRowOptions, paginatorLinks, showingEntries, Skeletonitems, SkeletonbodyTemplate, filterDegreeList } from '../../appconfig/Settings';

// Services
import { getAllDetails, getAllDetailsDirect, postAllData } from '../../service/ProductService';

// Breadcrumb
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';

// API constants
import { APP_HOSPITAL, GET_COLLEGES, GET_PCI_APPROVAL, PCI_APPROVAL_STATUS, UNIVERSITY } from '../../config/ApiConstant';
import { CollegeManageTab } from './CollegeManageTab';
import { Badge } from 'primereact/badge';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { convertDate, convertMonthFormat } from '../../appconfig/AppHelper';
import { PendingApprovalMasterValidate } from '../../config/Validate';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import CustomDatePicker from '../../components/CustomDatePicker/CustomDatePicker';

export const PciApproval = () => {
    document.title = 'PCI Approval | GSPC';

    // navigate
    const navigate = useNavigate();

    // Toast & filters
    const toast = useRef<Toast | null>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<string | null>(null);

    // Loading/data states
    const [loading, setLoading] = useState(false);
    const [pciList, setPciList] = useState<any[]>([]);
    const [colleges, setColleges] = useState<any[]>([]);
    const [selectedCollege, setSelectedCollege] = useState<any>(null);
    const [statusValue, setStatusValue] = useState<any>(null);
    const [degreeValue, setDegreeValue] = useState<any>(null);

    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);

    const [universityList, setUniversityList] = useState<any>([]);
    const [hospitalList, setHospitalList] = useState<any>([]);
    const [collegeId, setCollegeId] = useState<any>(null);

    const [approvalData, setApprovalData] = useState<any>({});
    const [date, setDate] = useState<any>();
    const [errors, setErrors] = useState<any>({});
    const [submitLoading, setSubmitLoading] = useState(false);

    const [fromDates, setFromDates] = useState({
        start: new Date(),
        end: new Date()
    });

    useEffect(() => {
        getColleges();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // ufeeffect
    useEffect(() => {
        getAllUniversity();
        getAllHospital();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Fetch all colleges for dropdown
    const getColleges = async () => {
        try {
            const response = await getAllDetails(`${GET_COLLEGES}?type=all`);
            if (response) {
                const collegeData = response.map((c: any) => ({
                    name: c.orgnameeng,
                    code: c.orgcode
                }));
                setColleges(collegeData);
            } else {
                setColleges([]);
            }
        } catch {
            setColleges([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Could not load colleges'
            });
        }
    };

    // Fetch PCI approval list (optionally by college)
    const getPciApprovalList = async (collegeId?: string) => {
        try {
            setLoading(true);
            const params: any = { type: 'Admin' };
            if (collegeId) params.college_id = collegeId;

            const url = `${GET_PCI_APPROVAL}?${new URLSearchParams(params)}`;
            const response = await getAllDetails(url);
            setPciList(response || []);
        } catch {
            setPciList([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Sorry, could not load data'
            });
        } finally {
            setLoading(false);
        }
    };

    // onsubmit api call
    const onSubmitStatus = async () => {
        try {
            const { errors: vErrors, isError } = PendingApprovalMasterValidate(approvalData, selectedRow);
            setErrors(vErrors);
            if (isError) return;

            setSubmitLoading(true);

            const pciCircularDateConvert = convertDate(date);
            const approvalYearFromCon = convertMonthFormat(fromDates.start);
            const approvalYearToCon = convertMonthFormat(fromDates.end);

            const fromDateApproval = new Date(fromDates.start);
            const validMonthF = fromDateApproval.getMonth() + 1;
            const validYearF = fromDateApproval.getFullYear();

            const toDateApproval = new Date(fromDates.end);
            const validMonth = toDateApproval.getMonth() + 1;
            const validYear = toDateApproval.getFullYear();

            // req data obj
            const requestData = {
                id: selectedRow?.id,
                orgcode: selectedRow?.college_id,
                orgNm: selectedRow?.college_name,
                university: approvalData?.university?.name,
                USrNo: approvalData?.university?.code,
                examauthoSrNo: approvalData?.university?.code,
                examautho: approvalData?.university?.name,
                DegNm: approvalData?.degree?.name || selectedRow?.degree_name,
                degree: approvalData?.degree?.code || selectedRow?.degree_id,
                pcirefno: `PCI/${selectedRow?.pci_code}`,
                pciresno: approvalData?.pciResNo,
                pcicirdt: pciCircularDateConvert,
                Seat: approvalData?.seatsTotal,
                HospNo1: approvalData?.hospital?.code || '',
                HospNo2: approvalData?.hospital?.name || '',
                qualdateorg: approvalYearFromCon,
                qualdateorgf: approvalYearToCon,
                validmonth: validMonthF,
                validyear: validYearF,
                validmonthf: validMonth,
                validyearf: validYear
            };

            const response = await postAllData(PCI_APPROVAL_STATUS, requestData);
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'College has been approved' });
                setDialogVisible(false);
                setApprovalData({});
                getPciApprovalList(collegeId);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Status update failed' });
        } finally {
            setSubmitLoading(false);
        }
    };

    // get all university api call
    const getAllUniversity = async () => {
        try {
            const response = await getAllDetailsDirect(UNIVERSITY + '?type=all');
            const list = (response || []).map((u: any) => ({ name: u.UName, code: u.SrNo }));
            setUniversityList(list);
        } catch {
            setUniversityList([]);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load universities' });
        }
    };

    // get all hospital api
    const getAllHospital = async () => {
        try {
            const response = await getAllDetailsDirect(APP_HOSPITAL + '?type=all');
            const list = (response || []).map((h: any) => ({ name: h.HospName, code: h.HospNo }));
            setHospitalList(list);
        } catch {
            setHospitalList([]);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load hospitals' });
        }
    };

    // Reset filters & data
    const clearAll = () => {
        setSelectedCollege(null);
        setCollegeId(null)
        setPciList([]);
        setGlobalFieldFilter(null);
        setStatusValue(null);
        setGlobalFilter(null);
    };

    const onCollegeChange = (e: { value: any }) => {
        setSelectedCollege(e.value);
        if (e.value) {
            getPciApprovalList(e.value.code);
            setCollegeId(e.value.code);
        } else {
            setPciList([]);
        }
    };

    // left toolbar
    const leftToolbarTemplate = () => (
        <span className="search-bar mr-3">
            <i className="pi pi-search"></i>
            <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
        </span>
    );

    // right toolbar
    const rightToolbarTemplate = () => (
        <div className="flex gap-2">
            <Dropdown value={selectedCollege} options={colleges} optionLabel="name" placeholder="Select College" onChange={onCollegeChange} className="mr-2 w-25rem" filter filterBy="name" filterPlaceholder="Search college..." showClear />
            <Button icon="pi pi-filter-slash" label="Clear" onClick={clearAll} className="p-button-outlined p-button-primary mr-2" />
            <Button label="Pending PCI Approval List" onClick={() => navigate('/app/pending-pci-approval-list')} className="p-button-warning mr-2" />
        </div>
    );

    // file template
    const fileTemplate = (row: any) => (row.approval_file ? <Button icon="pi pi-file" className="p-button-primary" onClick={() => window.open(row.approval_file, '_blank')} tooltip="Open File" tooltipOptions={{ position: 'top' }} /> : null);

    // status template
    const statusTemplate = (row: any) => {
        const approved = Number(row.status) === 1;
        return (
            <div className="flex flex-column align-items-center">
                <InputSwitch checked={approved} className="mb-2" disabled={approved} onChange={(e) => handleStatusChange(row, e.value)} />
                <Badge value={approved ? 'Approved' : 'Pending'} severity={approved ? 'success' : 'warning'} />
            </div>
        );
    };

    // status filter template
    const statusFilterTemplate = (options: any) => {
        return (
            <Dropdown
                filter
                value={statusValue}
                options={statusFilterOptions}
                onChange={(e) => {
                    setStatusValue(e.value);
                    options.filterCallback(e.value.code, options.index);
                }}
                placeholder="Filter by status"
                className="p-column-filter"
                optionLabel="name"
                showClear
            />
        );
    };

    //  filter by degree template
    const filterByDegree = (options: any) => {
        return (
            <Dropdown
                filter
                value={degreeValue}
                options={filterDegreeList}
                onChange={(e) => {
                    setDegreeValue(e.value);
                    options.filterCallback(e.value ? e.value.name : null, options.index);
                }}
                placeholder="Filter by status"
                className="p-column-filter"
                optionLabel="name"
                showClear
            />
        );
    };

    // status change funtion to open dialog
    const handleStatusChange = (row: any, value: boolean) => {
        setSelectedRow(row);
        setDialogVisible(true);
    };

    // on date change
    const onFromDateChange = (start: any, end: any) => setFromDates({ start, end });

    // on hide dialog
    const onHide = () => {
        setDialogVisible(false);
        setApprovalData({});
        setErrors({});
    };

    // on input change
    const onInputChange = (e: any, name: string) => {
        let val;
        if (name === 'university' || name === 'hospital') {
            val = e;
        } else if (name === 'seatsTotal') {
            val = e.value;
        } else {
            val = (e.target && e.target.value) || e.value || '';
        }
        setApprovalData({ ...approvalData, [name]: val });
    };

    return (
        <>
            <CollegeManageTab mainTabLabel="PCI Approval" />
            <Toast ref={toast} />
            <AppBreadcrumb currentMenu="PCI Approval" pageName="Approved PCI Approval List" pageTitle="Approved PCI Approval List" />
            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />

                        {!loading ? (
                            <DataTable
                                value={pciList}
                                showGridlines
                                stripedRows
                                globalFilter={globalFilter}
                                filters={globalFieldFilter}
                                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                rows={defaultRowOptions}
                                paginator={pciList.length >= 10}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm"
                                paginatorTemplate={paginatorLinks}
                                currentPageReportTemplate={showingEntries}
                                emptyMessage={selectedCollege ? 'No data available for the selected college' : 'No Data available'}
                                removableSort
                            >
                                {PciListColumns.map((col, i) => {
                                    if (col.field === 'sr_no') {
                                        return <Column key={col.field} header={col.header} body={(_: any, { rowIndex }) => rowIndex + 1} />;
                                    } else if (col.field === 'approval_file') {
                                        return <Column key={col.field} header={col.header} body={fileTemplate} />;
                                    } else if (['from_year', 'to_year'].includes(col.field)) {
                                        return <Column key={col.field} field={col.field} header={col.header} filter filterPlaceholder={`Search by ${col.header}`} sortable />;
                                    } else if (col.field === 'degree_name') {
                                        return <Column key={col.field} field={col.field} header={col.header} filter filterElement={filterByDegree} />;
                                    } else if (col.field === 'pci_code') {
                                        return <Column key={col.field} field={col.field} header={col.header} filter filterPlaceholder={`Search by ${col.header}`} />;
                                    } else if (col.field === 'status') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={statusTemplate} filter filterElement={statusFilterTemplate} />;
                                    } else {
                                        return <Column key={col.field} field={col.field} header={col.header} />;
                                    }
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems}>
                                {PciListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                header="Confirm PCI Approval"
                visible={dialogVisible}
                draggable={false}
                style={{ width: '90vw', maxWidth: '80%' }}
                onHide={onHide}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={onHide} />
                        <Button label="Submit" icon="pi pi-check" className="p-button-success" loading={submitLoading} onClick={onSubmitStatus} />
                    </div>
                }
            >
                <div className="p-fluid formgrid grid">
                    <div className="field col-12 md:col-4">
                        <label>College *</label>
                        <InputText value={selectedRow?.college_name} disabled />
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>University *</label>
                        <Dropdown
                            value={approvalData?.university}
                            options={universityList}
                            filter
                            optionLabel="name"
                            placeholder="Select University"
                            onChange={(e) => onInputChange(e.value, 'university')}
                            className={errors['university'] && 'p-invalid'}
                        />
                        <small className="p-invalid-txt">{errors['university']}</small>
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>Degree *</label>
                        <InputText value={selectedRow?.degree_name} disabled />
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>PCI Res No *</label>
                        <InputText value={approvalData?.pciResNo || ''} placeholder="Enter PCI Res No" onChange={(e) => onInputChange(e, 'pciResNo')} className={errors['pciResNo'] && 'p-invalid'} />
                        <small className="p-invalid-txt">{errors['pciResNo']}</small>
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>PCI Ref No *</label>
                        <InputText
                            value={`PCI/${selectedRow?.pci_code || ''}`}
                            disabled
                            // onChange={(e) => {
                            //     onInputChange(e, 'pciRefNo');
                            // }}
                        />
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>PCI Circular Date</label>
                        <Calendar value={date} dateFormat="dd-M-yy" onChange={(e) => setDate(e.value)} showIcon />
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>Seat</label>
                        <InputNumber value={approvalData?.seatsTotal || ''} placeholder="Enter Seat" onChange={(e) => onInputChange(e, 'seatsTotal')} className={errors['seatsTotal'] && 'p-invalid'} />
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>From and To Date</label>
                        <CustomDatePicker startDate={fromDates.start} endDate={fromDates.end} onChange={onFromDateChange} />
                    </div>

                    <div className="field col-12 md:col-4">
                        <label>Approved Hospital</label>
                        <Dropdown value={approvalData?.hospital} options={hospitalList} filter optionLabel="name" placeholder="Select Hospital" onChange={(e) => onInputChange(e.value, 'hospital')} className={errors['hospital'] && 'p-invalid'} />
                    </div>
                </div>
            </Dialog>
        </>
    );
};
